// The shared portal language (client-app/shared.jsx): outcome pills, check icons, status pill, banners, the
// page header, hints, key-value blocks, folds and row actions.
import React, { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Badge, Button, Icon, IconButton, type ButtonVariant } from "../ui";
import { OUTCOMES, STEPS, type Outcome } from "./format";

export const mono: CSSProperties = { fontFamily: "var(--font-body)", fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
export const th: CSSProperties = { font: "var(--type-overline)", letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-muted)", textAlign: "left", padding: "11px 14px", whiteSpace: "nowrap", background: "var(--surface-sunken)", borderTop: "1px solid var(--border-default)", borderBottom: "1px solid var(--border-default)" };
export const td: CSSProperties = { padding: "13px 14px", borderTop: "1px solid var(--border-subtle)", verticalAlign: "middle", font: "var(--type-body-sm)", color: "var(--text-body)" };
export const overline: CSSProperties = { font: "var(--type-overline)", letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-muted)" };
export const muted: CSSProperties = { font: "var(--type-body-sm)", color: "var(--text-muted)" };
/** A card's title: small uppercase in the dark body grey, so it never bleeds into bold row text. The same style labels a single figure. */
export const cardTitle: CSSProperties = { font: "800 12px/1.3 var(--font-body)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--text-body)" };
export const table: CSSProperties = { width: "100%", borderCollapse: "collapse" };

export const TIPS: Record<string, string> = {
  "Check again": "Reads the PO from Syncore again and re-runs all 13 checks",
  "Update PO freight": "Updates the PO in Syncore to the invoice value, then checks the invoice again",
  "Reject invoice…": "Keeps the record as Not approved with your reason; nothing changes in Syncore",
  "Upload a corrected invoice…": "The new PDF is checked against the PO; this one is marked Not approved as replaced",
};

/** A tooltip rendered to the body so it isn't clipped by tables or drawers. */
export function Hint({ text, children, width = 240, below }: { text: ReactNode; children: ReactNode; width?: number; below?: boolean }) {
  const [pos, setPos] = useState<{ left: number; top: number; w: number; dn: boolean } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const show = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = Math.min(width, window.innerWidth - 16);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - w - 8);
    let dn = !!below;
    if (dn && r.bottom + 120 > window.innerHeight) dn = false;
    if (!dn && r.top < 120) dn = true;
    setPos({ left, w, top: dn ? r.bottom + 8 : r.top - 8, dn });
  };
  useEffect(() => {
    if (!pos) return;
    const hide = () => setPos(null);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => { window.removeEventListener("scroll", hide, true); window.removeEventListener("resize", hide); };
  }, [pos]);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }} onMouseEnter={show} onMouseLeave={() => setPos(null)} onFocus={show} onBlur={() => setPos(null)}>
      {children}
      {pos && createPortal(
        <span role="tooltip" style={{ position: "fixed", zIndex: 400, left: pos.left, top: pos.top, transform: pos.dn ? "none" : "translateY(-100%)", width: pos.w, padding: "8px 11px", borderRadius: 10, background: "var(--surface-inverse)", color: "#fff", font: "600 12px/1.4 var(--font-body)", boxShadow: "var(--shadow-md)", pointerEvents: "none", whiteSpace: "normal", textTransform: "none", letterSpacing: 0, textAlign: "left" }}>{text}</span>,
        document.body,
      )}
    </span>
  );
}

export function OutcomePill({ o, size = "md", below, readyLine }: { o: Outcome; size?: "sm" | "md"; below?: boolean; readyLine?: string }) {
  const x = OUTCOMES[o];
  return (
    <Hint text={o === "ready" && readyLine ? "Matched its PO. " + readyLine + "." : x.meaning} below={below}>
      <Badge tone={x.tone} size={size} dot={false}>{x.label}</Badge>
    </Hint>
  );
}

export type CheckResult = "ok" | "adj" | "no" | "na";
const CHECK_ICON: Record<CheckResult, [string, string, string]> = {
  ok: ["var(--av-mint-500)", "check", "#fff"],
  adj: ["var(--av-sun-400)", "check", "var(--av-navy-900)"],
  no: ["var(--av-coral-500)", "x", "#fff"],
  na: ["transparent", "minus", "var(--av-gray-400)"],
};
const CHECK_TIP: Record<CheckResult, string> = { ok: "Passed", adj: "Passed: a difference was allowed by your rules", no: "Failed", na: "Not checked" };

export function CheckIcon({ r, size = 18 }: { r: CheckResult; size?: number }) {
  const [bg, ic, fg] = CHECK_ICON[r];
  return (
    <span title={CHECK_TIP[r]} style={{ flex: "none", width: size, height: size, borderRadius: "50%", background: bg, boxShadow: r === "na" ? "inset 0 0 0 1.5px var(--av-gray-300)" : "none", color: fg, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <Icon name={ic} size={Math.round(size * 0.62)} stroke={3.4} />
    </span>
  );
}

/** The header row of a card: the title, a count or subtitle beside it in sentence case, anything on the right, and a hairline closing the row. `pad` is the card's padding: "lg" pulls the row out to the card's edges. */
export function CardHead({ title, count, sub, right, pad }: { title: ReactNode; count?: number | null; sub?: ReactNode; right?: ReactNode; pad: "lg" | "none" }) {
  const box: CSSProperties = pad === "lg" ? { margin: "calc(var(--space-6) * -1) calc(var(--space-6) * -1) 0", padding: "16px var(--space-6) 12px" } : { padding: "16px 20px 12px" };
  return (
    <div className="avp-cardhead" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", borderBottom: "1px solid var(--border-subtle)", ...box }}>
      <span style={cardTitle}>{title}</span>
      {count != null && <span style={{ font: "600 13px var(--font-body)", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>{count}</span>}
      {sub && <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{sub}</span>}
      {right && <><span style={{ flex: 1 }} />{right}</>}
    </div>
  );
}

export type SystemState = "Running" | "Delayed" | "Paused" | "Off" | "Preview";

export function StatusPill({ state }: { state: SystemState }) {
  const M: Record<SystemState, [string, string, string, string]> = {
    Running: ["var(--status-matched-bg)", "var(--status-matched-fg)", "var(--status-matched)", "Reading and checking invoices as they arrive."],
    Delayed: ["var(--status-review-bg)", "var(--status-review-fg)", "var(--av-sun-400)", "Running slower than usual. Nothing needed from you."],
    Paused: ["var(--status-neutral-bg)", "var(--status-neutral-fg)", "var(--av-gray-400)", "Paused by AutoVouch support. Invoices are queued."],
    Off: ["var(--status-mismatch-bg)", "var(--status-mismatch-fg)", "var(--av-coral-500)", "Not checking invoices until the Syncore API connection is tested."],
    Preview: ["var(--av-blue-50)", "var(--av-blue-700)", "var(--av-blue-500)", "Checking invoices; nothing changes in Syncore."],
  };
  const m = M[state];
  return (
    <Hint below width={250} text={m[3]}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 28, padding: "0 12px", borderRadius: 99, background: m[0], color: m[1], font: "800 13px var(--font-body)" }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: m[2], animation: state === "Running" ? "avp-pulse 2s var(--ease-out) infinite" : "none" }} />
        {state === "Preview" ? "Preview mode" : state}
      </span>
    </Hint>
  );
}

export type BannerTone = "warn" | "bad" | "info" | "neutral";

export function Banner({ tone, icon, title, text, action }: { tone: BannerTone; icon: string; title: ReactNode; text?: ReactNode; action?: ReactNode }) {
  const T: Record<BannerTone, [string, string, string]> = {
    warn: ["var(--status-review-bg)", "var(--status-review-fg)", "rgba(255,191,71,.7)"],
    bad: ["var(--status-mismatch-bg)", "var(--status-mismatch-fg)", "rgba(242,95,92,.4)"],
    info: ["var(--av-blue-50)", "var(--av-blue-700)", "var(--av-blue-200)"],
    neutral: ["var(--surface-sunken)", "var(--text-body)", "var(--border-default)"],
  };
  const t = T[tone];
  return (
    <div role="status" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 16px", borderRadius: "var(--radius-lg)", background: t[0], boxShadow: "inset 0 0 0 1px " + t[2], color: t[1] }}>
      <Icon name={icon} size={18} />
      <b style={{ font: "800 14px var(--font-body)" }}>{title}</b>
      {text && <span style={{ font: "600 13px var(--font-body)" }}>{text}</span>}
      <span style={{ flex: 1 }} />
      {action}
    </div>
  );
}

export function PageHeader({ title, sub, children }: { title: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 24 }}>
      <h1 style={{ font: "800 30px/1.1 var(--font-display)", letterSpacing: "-0.02em", margin: 0 }}>{title}</h1>
      {sub && <span style={muted}>{sub}</span>}
      <span style={{ flex: 1 }} />
      {children}
    </div>
  );
}

export function KV({ rows, columns = "200px 1fr", style }: { rows: [ReactNode, ReactNode][]; columns?: string; style?: CSSProperties }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: columns, gap: "10px 16px", font: "14px var(--font-body)", alignItems: "center", ...style }}>
      {rows.map(([label, value], i) => (
        <React.Fragment key={i}>
          <span style={{ color: "var(--text-muted)" }}>{label}</span>
          <span style={{ color: "var(--text-strong)", fontWeight: 600 }}>{value}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

/** The dialogs' summary block: label left, figure right, on the sunken surface. */
export function SummaryKV({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px 20px", padding: "14px 16px", borderRadius: "var(--radius-md)", background: "var(--surface-sunken)", font: "14px var(--font-body)" }}>
      {rows.map(([label, value], i) => (
        <React.Fragment key={i}>
          <span style={{ color: "var(--text-muted)" }}>{label}</span>
          <b style={{ ...mono, color: "var(--text-strong)", textAlign: "right", whiteSpace: "normal" }}>{value}</b>
        </React.Fragment>
      ))}
    </div>
  );
}

export function Fold({ title, meta, children, open }: { title: ReactNode; meta?: ReactNode; children: ReactNode; open?: boolean }) {
  const [o, setO] = useState(!!open);
  return (
    <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-lg)", background: "var(--surface-card)" }}>
      <button type="button" onClick={() => setO(!o)} aria-expanded={o} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "13px 16px", border: 0, background: "none", cursor: "pointer", textAlign: "left", borderRadius: "var(--radius-lg)" }}>
        <Icon name="chevron-right" size={16} style={{ color: "var(--text-muted)", transform: o ? "rotate(90deg)" : "none", transition: "transform var(--dur-base) var(--ease-out)" }} />
        <span style={{ font: "700 14px var(--font-body)", color: "var(--text-strong)", flex: 1 }}>{title}</span>
        <span style={{ font: "13px var(--font-body)", color: "var(--text-muted)" }}>{meta}</span>
      </button>
      {o && <div style={{ padding: "2px 16px 16px", animation: "av-rise var(--dur-slow) var(--ease-out)" }}>{children}</div>}
    </div>
  );
}

export interface Action {
  label: string;
  icon?: string;
  variant?: ButtonVariant;
  onClick: () => void;
  disabled?: boolean;
}

/** One main action plus a "…" menu with the rest. */
export function RowActions({ primary, items = [], hidden }: { primary: Action; items?: Action[]; hidden?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  if (hidden) return null;
  return (
    <span ref={ref} onClick={(e) => e.stopPropagation()} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
      <Button size="sm" title={TIPS[primary.label]} variant={primary.variant || "primary"} disabled={primary.disabled} leadingIcon={primary.icon ? <Icon name={primary.icon} size={14} /> : null} onClick={primary.onClick}>{primary.label}</Button>
      {items.length > 0 && <IconButton size="sm" icon={<Icon name="ellipsis" size={18} />} label="More actions" onClick={() => setOpen(!open)} />}
      {open && (
        <span role="menu" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 40, minWidth: 200, padding: 6, borderRadius: "var(--radius-md)", background: "var(--surface-card)", boxShadow: "var(--shadow-lg)", display: "flex", flexDirection: "column", textAlign: "left" }}>
          {items.map((it) => (
            <button key={it.label} type="button" title={TIPS[it.label]} role="menuitem" className="avp-mi" disabled={it.disabled} onClick={() => { setOpen(false); it.onClick(); }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", border: 0, borderRadius: 8, background: "transparent", cursor: "pointer", font: "600 14px var(--font-body)", color: "var(--text-strong)", textAlign: "left", opacity: it.disabled ? 0.5 : 1 }}>
              {it.icon && <Icon name={it.icon} size={15} style={{ color: "var(--text-muted)" }} />}
              {it.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export function ProgressCompact({ step, big, label, fit }: { step: number; big?: boolean; label?: string | null; fit?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, font: (big ? "700 15px" : "600 13px") + " var(--font-body)", color: "var(--text-brand)", whiteSpace: "nowrap" }}>
      <span style={{ display: "inline-flex", gap: 3 }}>
        {[0, 1, 2, 3, 4].map((i) => <i key={i} style={{ width: big ? 22 : 13, height: big ? 7 : 5, borderRadius: 3, background: i < step ? "var(--av-mint-500)" : i === step ? "var(--av-blue-500)" : "var(--av-gray-100)", transition: "background var(--dur-slow)" }} />)}
      </span>
      <span style={{ display: "inline-block", width: fit ? "auto" : big ? 190 : 142, textAlign: "left", whiteSpace: fit ? "normal" : "nowrap" }}>{label || STEPS[step]}</span>
    </span>
  );
}

export function Spinner({ size = 16 }: { size?: number }) {
  return <Icon name="loader-circle" size={size} style={{ animation: "av-spin 1s linear infinite", color: "var(--text-brand)" }} />;
}

export function EmptyCard({ title, text, icon = "check", action }: { title: ReactNode; text?: ReactNode; icon?: string; action?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "var(--space-8)", background: "var(--surface-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-sm)" }}>
      <span style={{ width: 44, height: 44, borderRadius: 14, background: "var(--av-blue-50)", color: "var(--text-brand)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name={icon} size={20} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: "800 18px var(--font-display)", color: "var(--text-strong)" }}>{title}</div>
        {text && <div style={{ font: "var(--type-body)", color: "var(--text-muted)" }}>{text}</div>}
      </div>
      {action}
    </div>
  );
}

export function ErrorCard({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  return <Banner tone="bad" icon="circle-alert" title="Couldn’t load" text={message} action={retry && <Button size="sm" variant="outline" onClick={retry}>Try again</Button>} />;
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 20 }}>
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="avp-skeleton" style={{ height: 18, width: `${80 - i * 12}%` }} />)}
    </div>
  );
}

export function BackLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, border: 0, background: "none", padding: 0, cursor: "pointer", font: "700 14px var(--font-body)", color: "var(--text-brand)" }}>
      <Icon name="arrow-left" size={16} />{label}
    </button>
  );
}
